import axios from 'axios';
import { Order, OrderItem, SyncStatus, B2BProduct, LocalProduct } from '../types/orders';

export interface OrdersResponse {
  success: boolean;
  orders: Order[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
  };
}

export interface OrderItemsResponse {
  success: boolean;
  order: Order;
  items: OrderItem[];
}

export interface SyncOrdersResponse {
  success: boolean;
  synced: number;
  eligible: number;
}

export interface SyncProductsResponse {
  success: boolean;
  synced: number;
  unmapped: number;
}

export interface ProductsResponse {
  success: boolean;
  products: B2BProduct[];
}

export interface LocalProductsResponse {
  success: boolean;
  products: LocalProduct[];
}

export interface SyncStatusResponse {
  success: boolean;
  syncStatus: {
    orders?: SyncStatus;
    products?: SyncStatus;
  };
}

/**
 * Fetch orders with optional filters
 */
export const fetchOrders = async (
  filter: 'all' | 'pending' | 'hidden' | 'complete' = 'all',
  search?: string,
  limit: number = 100,
  includeHidden?: boolean
): Promise<OrdersResponse> => {
  const params = new URLSearchParams({
    filter,
    limit: limit.toString()
  });

  if (search) {
    params.append('search', search);
  }
  if (includeHidden) {
    params.append('includeHidden', 'true');
  }

  const { data } = await axios.get(`/api/b2b/orders?${params.toString()}`);
  return data;
};

/**
 * Fetch order items for a specific order
 */
export const fetchOrderItems = async (orderId: string): Promise<OrderItemsResponse> => {
  const { data } = await axios.get(`/api/b2b/orders/${orderId}/items`);
  return data;
};

/**
 * Fetch order status counts
 * Uses backend pagination totals to ensure counts match what's actually displayed
 */
export const fetchOrderStatusCounts = async (): Promise<Record<string, number>> => {
  const counts: Record<string, number> = {};
  
  try {
    // Fetch each filter's pagination total from the backend to ensure accuracy
    const statuses: Array<'all' | 'pending' | 'hidden' | 'complete'> = ['all', 'pending', 'hidden', 'complete'];
    await Promise.all(statuses.map(async (status) => {
      try {
        const response = await axios.get(`/api/b2b/orders?filter=${status}&limit=1`);
        counts[status] = response.data.pagination?.total || response.data.orders?.length || 0;
      } catch (err) {
        console.error(`Error fetching count for status ${status}:`, err);
        counts[status] = 0;
      }
    }));
    
    return counts;
  } catch (error) {
    console.error('Error fetching status counts:', error);
    // Return zeros if all requests fail
    return { all: 0, pending: 0, hidden: 0, complete: 0 };
  }
};

/**
 * Assign a serial number to an order item
 */
export const assignSerialToOrderItem = async (
  orderId: string,
  orderItemId: number,
  deviceId?: number,
  serialNumber?: string
): Promise<{ success: boolean; message?: string; error?: string }> => {
  const requestBody: any = { order_item_id: orderItemId };
  if (deviceId) {
    requestBody.device_id = deviceId;
  } else if (serialNumber) {
    requestBody.serial_number = serialNumber;
  }
  
  const { data } = await axios.post(`/api/b2b/orders/${orderId}/assign-serial`, requestBody);
  return data;
};

/**
 * Unassign a serial number from an order item.
 * @param hardDelete - If true, permanently removes the assignment with no history record.
 */
export const unassignSerial = async (assignmentId: number, reason?: string, hardDelete?: boolean): Promise<{ success: boolean }> => {
  const body: Record<string, any> = {};
  if (reason) body.reason = reason;
  if (hardDelete) body.hard_delete = true;
  const { data } = await axios.delete(`/api/b2b/orders/assignments/${assignmentId}`, {
    data: Object.keys(body).length > 0 ? body : undefined
  });
  return data;
};

/**
 * Delete an order by ID
 */
export const deleteOrder = async (orderId: number): Promise<{ success: boolean; message?: string }> => {
  const { data } = await axios.delete(`/api/b2b/orders/${orderId}`);
  return data;
};

/**
 * Set the hidden flag on one or more orders
 */
export const setOrdersHidden = async (
  orderIds: number[],
  hidden: boolean
): Promise<{ success: boolean; count: number }> => {
  const { data } = await axios.post('/api/b2b/orders/set-hidden', { orderIds, hidden });
  return data;
};

